import { z } from "zod"

import type { CollectionKind } from "~/utils/collectionKind"
import { convertToStartOfWeekUTC } from "~/utils/datetime"
import { PLAN_PERIOD_WEEKS } from "~/utils/demandPlanPeriod"
import { WEEK_MS } from "~/utils/demandProjection"
import { INTRODUCED_WINDOW_WEEKS } from "~/utils/lifecycleUtils"

const DemandLifecycleSchema = z.enum(["upcoming", "live", "ongoing", "phasingOut", "phasedOut"])
export type DemandLifecycle = z.infer<typeof DemandLifecycleSchema>

/** NOOS Sales history over the plan period: how much own history it has (NOOS_LIFECYCLE.md). */
const SalesHistoryStageSchema = z.enum(["upcoming", "live", "mature"])
export type SalesHistoryStage = z.infer<typeof SalesHistoryStageSchema>

/** NOOS Lifecycle stage: whether it phases out or ends inside the plan period. */
const PlanLifecycleStageSchema = z.enum(["ongoing", "phasingOut", "phasedOut"])
export type PlanLifecycleStage = z.infer<typeof PlanLifecycleStageSchema>

/** What a variant's stages are read from: its lifecycle settings and its first sale. */
export type LifecycleDates = {
  lifecycleStatus?: string | null
  activatedFrom?: Date | null
  soldSince?: Date | null
  activatedUntil?: Date | null
  phasingOutFrom?: Date | null
}

/**
 * Where the variant's lifecycle starts: its activation date, else its first sale (as legacy NOOS
 * treats an undated variant). Never `isNpi`: the stage is a matter of dates alone.
 */
export function lifecycleStart(
  activatedFrom: Date | null | undefined,
  soldSince: Date | null | undefined,
): Date | null {
  return activatedFrom ?? soldSince ?? null
}

/**
 * An explicit phase-out wins. Then the start date alone: Upcoming until the week after its start's
 * week (or without one), Introduced from then until it has a full sales window behind it, Ongoing
 * after.
 * Mirrored per week by `lifecycleStatusAtWeek` on the server.
 */
export function deriveDemandLifecycle(
  lifecycleStatus: string | null | undefined,
  start: Date | null,
  asOf: Date,
): DemandLifecycle {
  if (lifecycleStatus === "phasedOut") return "phasedOut"
  if (lifecycleStatus === "phasingOut") return "phasingOut"
  // By week: Introduced once a whole week of sales since its start has ended, so a start in this
  // week is still Phasing in (NOOS_LIFECYCLE.md "Boundaries").
  const weekStart = convertToStartOfWeekUTC(asOf).getTime()
  if (start === null || new Date(start).getTime() >= weekStart) return "upcoming"
  if ((asOf.getTime() - start.getTime()) / WEEK_MS < INTRODUCED_WINDOW_WEEKS) return "live"
  return "ongoing"
}

export const demandLifecycle = (v: LifecycleDates, asOf: Date): DemandLifecycle =>
  deriveDemandLifecycle(v.lifecycleStatus, lifecycleStart(v.activatedFrom, v.soldSince), asOf)

const planPeriodEnd = (asOf: Date) =>
  convertToStartOfWeekUTC(asOf).getTime() + PLAN_PERIOD_WEEKS * WEEK_MS

/**
 * Sales history over the plan period, from the start date alone: Phasing in after this week (or
 * undated), New inside its first 52 weeks, Mature after. Null when it starts after the period: it
 * has no weeks in this plan.
 */
export function salesHistoryStage(v: LifecycleDates, asOf: Date): SalesHistoryStage | null {
  const start = lifecycleStart(v.activatedFrom, v.soldSince)
  if (start !== null && new Date(start).getTime() >= planPeriodEnd(asOf)) return null
  // No status passed, so only the start-date stages come back.
  const byStart = deriveDemandLifecycle(null, start, asOf)
  if (byStart === "upcoming" || byStart === "live") return byStart
  return "mature"
}

/**
 * Lifecycle stage over the plan period, from the phase-out dates and status alone. Phasing out when
 * the period overlaps Phasing out from → Activated until; else Phased out when it overlaps the
 * weeks after Activated until; else Ongoing. A status counts as one rule, not an override.
 */
export function planLifecycleStage(v: LifecycleDates, asOf: Date): PlanLifecycleStage {
  const periodStart = convertToStartOfWeekUTC(asOf).getTime()
  const periodEnd = planPeriodEnd(asOf)
  // End and phase-out dates go by their week.
  const weekOf = (d: Date | null | undefined) =>
    d == null ? null : convertToStartOfWeekUTC(new Date(d)).getTime()
  const until = weekOf(v.activatedUntil)
  const phasingFrom = weekOf(v.phasingOutFrom)
  // A phase-out after the end never applies (`phasingOutNeverApplies`).
  const phasingOverlaps =
    phasingFrom !== null &&
    (until === null || phasingFrom <= until) &&
    phasingFrom < periodEnd &&
    (until === null || until >= periodStart)
  if (v.lifecycleStatus === "phasingOut" || phasingOverlaps) return "phasingOut"
  if (v.lifecycleStatus === "phasedOut" || (until !== null && until < periodEnd)) return "phasedOut"
  return "ongoing"
}

/**
 * What the stages were read from, as ISO dates; `activatedFrom` falls back to the first sale and the
 * end dates go by their week. Absent on publishes stored before it.
 */
const LifecycleInputsSchema = z.object({
  lifecycleStatus: z.string().nullable(),
  activatedFrom: z.string().nullable(),
  activatedUntil: z.string().nullable(),
  phasingOutFrom: z.string().nullable(),
})
export type LifecycleInputs = z.infer<typeof LifecycleInputsSchema>

/** A variant's stages as its plan reads them: NOOS has two statuses, fashion one. */
export const PlanLifecycleSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("noos"),
    salesHistory: SalesHistoryStageSchema.nullable(),
    lifecycleStage: PlanLifecycleStageSchema,
    inputs: LifecycleInputsSchema.optional(),
  }),
  z.object({
    kind: z.literal("fashion"),
    lifecycle: DemandLifecycleSchema,
    inputs: LifecycleInputsSchema.optional(),
  }),
])
export type PlanLifecycle = z.infer<typeof PlanLifecycleSchema>

export const planLifecycle = (
  kind: CollectionKind,
  v: LifecycleDates,
  asOf: Date,
): PlanLifecycle =>
  kind === "noos"
    ? {
        kind,
        salesHistory: salesHistoryStage(v, asOf),
        lifecycleStage: planLifecycleStage(v, asOf),
      }
    : { kind, lifecycle: demandLifecycle(v, asOf) }
